import type { ReactNode } from 'react';

import { Button } from './button';
import { Dialog } from './dialog';
import { InlineError } from './inline-error';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 질문형 — D5 §4. 예: "이 할 일을 그만둘까요?" */
  title: string;
  description?: ReactNode;
  /** 동작 동사. "확인"·"예"는 쓰지 않는다 — D5 §4. */
  confirmLabel: string;
  /** @default '취소' */
  cancelLabel?: string;
  /** @default 'default' */
  tone?: 'default' | 'danger';
  /** 확정 버튼이 스피너를 보이고 다시 눌리지 않는다(`Button pending`). 대화상자는 열린 채로 둔다. */
  pending?: boolean;
  error?: ReactNode;
  /** 성공 시 대화상자를 닫는 것은 호출자의 몫이다 — 여기서 자동으로 닫지 않는다. */
  onConfirm: () => void;
  /** 사유 입력 등 추가 필드. 설명 아래 · 오류 위에 놓인다. */
  children?: ReactNode;
  /** @default 'sm' */
  size?: 'sm' | 'md' | 'lg';
}

/** 되돌릴 수 없거나 확인이 필요한 동작 앞에 세우는 대화상자. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = '취소',
  tone = 'default',
  pending,
  error,
  onConfirm,
  children,
  size = 'sm',
}: ConfirmDialogProps) {
  const hasBody = description !== undefined || children !== undefined || error !== undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content title={title} size={size}>
        {hasBody ? (
          <div className="gap-card flex flex-col">
            {description !== undefined ? (
              <Dialog.Description>{description}</Dialog.Description>
            ) : null}
            {children}
            {error !== undefined ? <InlineError>{error}</InlineError> : null}
          </div>
        ) : null}
        <Dialog.Footer>
          <Dialog.Close render={<Button variant="outline">{cancelLabel}</Button>} />
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            pending={pending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
